import { useQueries } from '@tanstack/react-query'
import { findResourceOfType, globalQueryKeys } from '@opallabs/api'
import { moments } from '@opallabs/api/v3'
import { type ResolveImageUrl } from '../types'

/**
 * Moments per request. Each one is named in the address by its uuid, and forty
 * of those keep the address inside the ~2,000 characters it can be relied on
 * to carry.
 */
const CHUNK_SIZE = 40

const chunk = <T>(list: readonly T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(list.length / size) }, (_, index) =>
    list.slice(index * size, (index + 1) * size)
  )

/**
 * The cover picture of each moment, by id: the preview of its primary asset.
 * A moment with no primary asset is `null`.
 */
const readCovers = async (
  momentIds: string[]
): Promise<ReadonlyMap<string, string | null>> => {
  const document = await moments.readCollection({
    query: {
      filter: { id: momentIds },
      include: ['primary_asset_reference'],
      page: { limit: CHUNK_SIZE },
    },
  })

  return new Map(
    (document.data ?? []).map((moment) => {
      const reference = document.included?.find(
        findResourceOfType(
          'asset_reference',
          'V3',
          moment.relationships?.primary_asset_reference?.data?.id
        )
      )

      return [moment.id, reference?.attributes.preview_url ?? null]
    })
  )
}

type Waiter = {
  reject: (error: unknown) => void
  resolve: (cover: string | null) => void
}

let waiting = new Map<string, Waiter[]>()

const readWaitingCovers = () => {
  const batch = waiting
  waiting = new Map()

  chunk([...batch.keys()], CHUNK_SIZE).forEach((ids) => {
    readCovers(ids).then(
      (covers) =>
        ids.forEach((id) =>
          batch
            .get(id)
            ?.forEach(({ resolve }) => resolve(covers.get(id) ?? null))
        ),
      (error: unknown) =>
        ids.forEach((id) =>
          batch.get(id)?.forEach(({ reject }) => reject(error))
        )
    )
  })
}

/**
 * One moment's cover, read together with every other cover asked for in the
 * same tick. React Query starts the queries of one render, and the refetches
 * of one invalidation, all at once, so a panel's moments share a request.
 */
const readCover = (momentId: string): Promise<string | null> =>
  new Promise((resolve, reject) => {
    if (waiting.size === 0) void Promise.resolve().then(readWaitingCovers)
    waiting.set(momentId, [
      ...(waiting.get(momentId) ?? []),
      { reject, resolve },
    ])
  })

/**
 * The cover pictures of the moments a panel shows, for the tiles on the day
 * panel's rows and the hero of an open moment.
 *
 * The month payload carries no pictures, and a month can hold far more moments
 * than anyone opens, so only the moments asked for are read. Each is cached on
 * its own, so a moment already read is not asked for again when another panel
 * shows it. Content is not answered here: its snapshot comes from
 * `usePanelSnapshot`.
 *
 * A moment that was asked for is `undefined` until the answer arrives, so a
 * panel can wait for it; one that was never asked for is `null`.
 */
export const useMomentCovers = (
  momentIds: readonly string[]
): ResolveImageUrl => {
  const results = useQueries({
    queries: momentIds.map((momentId) => ({
      queryFn: () => readCover(momentId),
      queryKey: globalQueryKeys.momentCovers(momentId),
    })),
  })

  return (item) => {
    const index = item.kind === 'moment' ? momentIds.indexOf(item.id) : -1
    if (index === -1 || results[index].isError) return null

    return results[index].data
  }
}
